/**
 * The order page's tabs, and the Turkish word each is addressed by in
 * `?sekme=`: a notice that a buyer asked to cancel, and a row on İadeler, lead to
 * the tab where the request is answered rather than to the page's first tab and
 * a search for it. The address is also what the page reads its tab from, so a
 * link followed from within the page itself changes the tab too.
 */
const SECTIONS = {
    ozet: 'summary',
    kargo: 'shipping',
    talepler: 'claims',
    fatura: 'invoice',
    gecmis: 'timeline',
} as const;

export type OrderSection = keyof typeof SECTIONS;
export type OrderTab = (typeof SECTIONS)[OrderSection];

/** Where a link into an order lands: a tab. */
export interface OrderPageSearch {
    sekme?: OrderSection;
}

/**
 * The route's reading of its address: anything it does not know is dropped. Own
 * keys only, so a word that is a property of every object (`toString`) is as
 * unknown as any other.
 */
export function validateOrderSearch(search: Record<string, unknown>): OrderPageSearch {
    return typeof search.sekme === 'string' && Object.hasOwn(SECTIONS, search.sekme) ? { sekme: search.sekme as OrderSection } : {};
}

/**
 * The tab an order page opens on. Its word is checked again here: the router
 * merges the route's reading with the address's own parameters, so a word the
 * route dropped still reaches the page, and named no tab at all.
 */
export function orderTab(search: OrderPageSearch): OrderTab {
    const { sekme } = validateOrderSearch({ sekme: search.sekme });
    return sekme ? SECTIONS[sekme] : 'summary';
}

/**
 * The address a tab is written to. The first tab is the page's own address, so
 * choosing it leaves no `?sekme=` behind.
 */
export function orderSearchFor(tab: string): OrderPageSearch {
    const section = (Object.keys(SECTIONS) as OrderSection[]).find((candidate) => SECTIONS[candidate] === tab);
    return section && section !== 'ozet' ? { sekme: section } : {};
}
